// ConditionBuilder（Phase 4.4）：10 种 condition 自由组合编辑器。
// - 受控 value/onChange，外层 form 可以 Controller 包；这里走最简纯 controlled。
// - 每行根据 type 渲染不同字段；新增/删除/重排。
// - 输出始终是合法的 Condition[]，调用方直接 JSON.stringify 给 daemon。

import { useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Input";
import {
  CONDITION_OPS,
  CONDITION_TYPE_LABELS,
  defaultsForType,
  type Condition,
  type ConditionType,
} from "./schema";

export interface ConditionBuilderProps {
  value: Condition[];
  onChange: (next: Condition[]) => void;
  className?: string;
}

const TYPE_OPTIONS = Object.keys(CONDITION_TYPE_LABELS) as ConditionType[];

export function ConditionBuilder({
  value,
  onChange,
  className,
}: ConditionBuilderProps) {
  const items = useMemo(() => value ?? [], [value]);

  function updateAt(index: number, next: Condition) {
    onChange(items.map((it, i) => (i === index ? next : it)));
  }
  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }
  function appendType(type: ConditionType) {
    onChange([...items, defaultsForType(type)]);
  }
  function changeType(index: number, type: ConditionType) {
    updateAt(index, defaultsForType(type));
  }

  return (
    <div className={className}>
      <div className="space-y-2">
        {items.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            没有条件。下方可添加。
          </p>
        ) : null}
        {items.map((cond, idx) => (
          <ConditionRow
            key={idx}
            index={idx}
            value={cond}
            onChange={(next) => updateAt(idx, next)}
            onChangeType={(t) => changeType(idx, t)}
            onRemove={() => removeAt(idx)}
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[hsl(var(--muted-foreground))]">添加：</span>
        {TYPE_OPTIONS.map((t) => (
          <Button
            key={t}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => appendType(t)}
          >
            + {CONDITION_TYPE_LABELS[t]}
          </Button>
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  index: number;
  value: Condition;
  onChange: (next: Condition) => void;
  onChangeType: (t: ConditionType) => void;
  onRemove: () => void;
}

function ConditionRow({ index, value, onChange, onChangeType, onRemove }: RowProps) {
  return (
    <div className="grid grid-cols-1 gap-2 rounded border border-[hsl(var(--border))] p-3 md:grid-cols-12">
      <div className="md:col-span-3">
        <Field label={`#${index + 1} 类型`}>
          <select
            value={value.type}
            onChange={(e) => onChangeType(e.target.value as ConditionType)}
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {CONDITION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="md:col-span-8">
        <TypeFields value={value} onChange={onChange} />
      </div>
      <div className="md:col-span-1 md:self-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRemove}
          aria-label="移除条件"
        >
          移除
        </Button>
      </div>
    </div>
  );
}

function TypeFields({
  value,
  onChange,
}: {
  value: Condition;
  onChange: (next: Condition) => void;
}) {
  switch (value.type) {
    case "min_level":
    case "min_reincarnation":
    case "min_evolution_level":
      return (
        <Field label="数值">
          <Input
            type="number"
            min={0}
            value={value.value}
            onChange={(e) =>
              onChange({ ...value, value: numOrZero(e.target.value) })
            }
          />
        </Field>
      );
    case "server_open_days":
    case "vip_level":
    case "combat_power":
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field label="op">
            <OpSelect
              value={value.op}
              onChange={(op) => onChange({ ...value, op })}
            />
          </Field>
          <Field label={numberFieldLabel(value.type)}>
            <Input
              type="number"
              min={0}
              value={value.value}
              onChange={(e) =>
                onChange({ ...value, value: numOrZero(e.target.value) })
              }
            />
          </Field>
        </div>
      );
    case "item_count":
      return (
        <div className="grid grid-cols-4 gap-2">
          <Field label="item_id">
            <Input
              value={value.item_id ?? ""}
              onChange={(e) =>
                onChange({ ...value, item_id: e.target.value })
              }
            />
          </Field>
          <Field label="item_name">
            <Input
              value={value.item_name ?? ""}
              onChange={(e) =>
                onChange({ ...value, item_name: e.target.value })
              }
            />
          </Field>
          <Field label="op">
            <OpSelect
              value={value.op}
              onChange={(op) => onChange({ ...value, op })}
            />
          </Field>
          <Field label="count">
            <Input
              type="number"
              min={0}
              value={value.count}
              onChange={(e) =>
                onChange({ ...value, count: numOrZero(e.target.value) })
              }
            />
          </Field>
        </div>
      );
    case "equipment_multiplier":
      return (
        <div className="grid grid-cols-4 gap-2">
          <Field label="item_id">
            <Input
              value={value.item_id ?? ""}
              onChange={(e) =>
                onChange({ ...value, item_id: e.target.value })
              }
            />
          </Field>
          <Field label="item_name">
            <Input
              value={value.item_name ?? ""}
              onChange={(e) =>
                onChange({ ...value, item_name: e.target.value })
              }
            />
          </Field>
          <Field label="op">
            <OpSelect
              value={value.op}
              onChange={(op) => onChange({ ...value, op })}
            />
          </Field>
          <Field label="倍数">
            <Input
              type="number"
              min={0}
              value={value.value}
              onChange={(e) =>
                onChange({ ...value, value: numOrZero(e.target.value) })
              }
            />
          </Field>
        </div>
      );
    case "currency_balance":
      return (
        <div className="grid grid-cols-3 gap-2">
          <Field label="currency">
            <Input
              value={value.currency}
              onChange={(e) =>
                onChange({ ...value, currency: e.target.value })
              }
            />
          </Field>
          <Field label="op">
            <OpSelect
              value={value.op}
              onChange={(op) => onChange({ ...value, op })}
            />
          </Field>
          <Field label="amount">
            <Input
              type="number"
              min={0}
              value={value.amount}
              onChange={(e) =>
                onChange({ ...value, amount: numOrZero(e.target.value) })
              }
            />
          </Field>
        </div>
      );
    case "currency_cost":
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field label="currency">
            <Input
              value={value.currency}
              onChange={(e) =>
                onChange({ ...value, currency: e.target.value })
              }
            />
          </Field>
          <Field label="amount">
            <Input
              type="number"
              min={0}
              value={value.amount}
              onChange={(e) =>
                onChange({ ...value, amount: numOrZero(e.target.value) })
              }
            />
          </Field>
        </div>
      );
  }
}

function numberFieldLabel(type: Condition["type"]) {
  switch (type) {
    case "server_open_days":
      return "天数";
    case "vip_level":
      return "VIP等级";
    case "combat_power":
      return "战斗力";
    default:
      return "数值";
  }
}

function OpSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (op: ">" | ">=" | "<" | "<=" | "==" | "!=") => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as never)}
      className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
    >
      {CONDITION_OPS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function numOrZero(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
}
